<template>
	<div class="sgnl-table">
		<!-- Column visibility selector (table layout only) -->
		<v-menu
			v-if="showColumnSelector && hideableColumns.length > 0 && !isCardLayout"
			location="bottom end"
			:close-on-content-click="false"
		>
			<template v-slot:activator="{ props }">
				<v-btn
					icon
					variant="text"
					size="x-small"
					v-bind="props"
					title="Column settings"
					class="sgnl-table__column-btn"
				>
					<Column16 />
				</v-btn>
			</template>
			<v-list density="compact">
				<v-list-item
					v-for="column in hideableColumns"
					:key="column.id"
				>
					<v-checkbox
						:model-value="column.getIsVisible()"
						@update:model-value="(value) => column.toggleVisibility(!!value)"
						:label="String(column.columnDef.header ?? column.id)"
						density="compact"
						hide-details
					/>
				</v-list-item>
			</v-list>
		</v-menu>

		<ErrorEmptyState
			v-if="error && data.length === 0"
			:title="errorTitle"
			:message="errorMessage"
			:retrying="retrying"
			bordered
			@retry="$emit('retry')"
		/>

		<div
			v-else
			class="sgnl-table__frame"
			:class="{ 'sgnl-table__frame--cards': hasCardSlot && isCardLayout }"
		>
			<!-- Desktop table -->
			<table class="sgnl-data-table">
				<thead v-if="!isEmpty">
					<tr>
						<th
							v-for="header in table.getHeaderGroups()[0].headers"
							:key="header.id"
							:class="[
								`col-${header.column.id}`,
								{ 'sgnl-data-table__th--sortable': header.column.getCanSort() },
							]"
							@click="header.column.getToggleSortingHandler()?.($event)"
						>
							<span class="sgnl-data-table__th-inner">
								<span>{{ header.column.columnDef.header }}</span>
								<ArrowUp16
									v-if="header.column.getIsSorted() === 'asc'"
									class="sgnl-data-table__sort-icon sgnl-data-table__sort-icon--active"
								/>
								<ArrowDown16
									v-else-if="header.column.getIsSorted() === 'desc'"
									class="sgnl-data-table__sort-icon sgnl-data-table__sort-icon--active"
								/>
								<ArrowsVertical16
									v-else-if="header.column.getCanSort()"
									class="sgnl-data-table__sort-icon"
								/>
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					<template v-if="loading && data.length === 0">
						<slot name="loading">
							<tr
								v-for="i in skeletonRowCount"
								:key="`skeleton-${i}`"
							>
								<td
									v-for="column in table.getVisibleLeafColumns()"
									:key="column.id"
									:class="`col-${column.id}`"
								>
									<SkeletonBlock width="70%" />
								</td>
							</tr>
						</slot>
					</template>
					<tr
						v-else-if="table.getRowModel().rows.length === 0"
						class="sgnl-data-table__empty-row"
					>
						<td
							:colspan="table.getVisibleLeafColumns().length"
							class="sgnl-data-table__empty"
						>
							<slot name="empty">
								<EmptyState
									:header="emptyText"
									:body="emptyBody"
									:icon="emptyIcon"
								/>
							</slot>
						</td>
					</tr>
					<template v-else>
						<tr
							v-for="row in table.getRowModel().rows"
							:key="rowKey ? rowKey(row.original) : row.id"
							:class="rowClass ? rowClass(row.original) : undefined"
							v-bind="rowAttrs ? rowAttrs(row.original) : undefined"
							@click="$emit('row-click', row.original)"
						>
							<td
								v-for="cell in row.getVisibleCells()"
								:key="cell.id"
								:class="`col-${cell.column.id}`"
							>
								<slot
									:name="`cell-${cell.column.id}`"
									:row="row.original"
									:value="cell.getValue()"
								>
									{{ cell.getValue() ?? "" }}
								</slot>
							</td>
						</tr>
					</template>
				</tbody>
			</table>

			<!-- Mobile card layout (rendered only when the consumer provides a card slot) -->
			<div
				v-if="hasCardSlot"
				class="sgnl-table__cards"
			>
				<template v-if="loading && data.length === 0">
					<div
						v-for="i in skeletonRowCount"
						:key="`card-skeleton-${i}`"
						class="sgnl-table__card"
					>
						<SkeletonBlock width="60%" />
						<SkeletonBlock width="40%" />
					</div>
				</template>
				<div
					v-else-if="table.getRowModel().rows.length === 0"
					class="sgnl-table__card-empty"
				>
					<slot name="empty">
						<EmptyState
							:header="emptyText"
							:body="emptyBody"
							:icon="emptyIcon"
						/>
					</slot>
				</div>
				<template v-else>
					<div
						v-for="row in table.getRowModel().rows"
						:key="rowKey ? `card-${rowKey(row.original)}` : `card-${row.id}`"
						class="sgnl-table__card"
						:class="rowClass ? rowClass(row.original) : undefined"
						@click="$emit('row-click', row.original)"
					>
						<slot
							name="card"
							:row="row.original"
						/>
					</div>
				</template>
			</div>

			<!-- Pagination footer -->
			<div
				v-if="paginated && table.getRowModel().rows.length > 0"
				class="sgnl-table__footer sgnl-paragraph-s-regular sgnl-text-color-secondary"
			>
				<label class="sgnl-table__page-size">
					<span>Rows per page</span>
					<select
						class="sgnl-table__page-size-select"
						:value="table.getState().pagination.pageSize"
						@change="onPageSizeChange"
					>
						<option
							v-for="size in pageSizeOptions"
							:key="size"
							:value="size"
						>
							{{ size }}
						</option>
					</select>
				</label>
				<div class="sgnl-table__pager">
					<span>{{ table.getState().pagination.pageIndex + 1 }} / {{ table.getPageCount() }}</span>
					<button
						type="button"
						class="sgnl-table__page-btn"
						:disabled="!table.getCanPreviousPage()"
						aria-label="Previous page"
						@click="table.previousPage()"
					>
						<ChevronLeft16 />
					</button>
					<button
						type="button"
						class="sgnl-table__page-btn"
						:disabled="!table.getCanNextPage()"
						aria-label="Next page"
						@click="table.nextPage()"
					>
						<ChevronRight16 />
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import Column16 from "@carbon/icons-vue/es/column/16";
import ArrowUp16 from "@carbon/icons-vue/es/arrow--up/16";
import ArrowDown16 from "@carbon/icons-vue/es/arrow--down/16";
import ArrowsVertical16 from "@carbon/icons-vue/es/arrows--vertical/16";
import ChevronLeft16 from "@carbon/icons-vue/es/chevron--left/16";
import ChevronRight16 from "@carbon/icons-vue/es/chevron--right/16";

import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import EmptyState from "@/components/Common/EmptyState.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";

import {
	useVueTable,
	getCoreRowModel,
	getSortedRowModel,
	getPaginationRowModel,
	type ColumnDef,
	type SortingState,
	type VisibilityState,
} from "@tanstack/vue-table";

/**
 * Reusable headless table built on TanStack Table. It owns the table skin,
 * sortable headers, loading/empty/error states, an optional column-visibility
 * selector, and an optional pagination footer. Consumers supply column
 * definitions plus per-column `cell-<columnId>` slots (and an optional `card`
 * slot for the mobile layout).
 *
 * The wrapper is intentionally column-type-agnostic (`any`), so each consumer
 * keeps its own strongly-typed `ColumnDef[]` at the call site.
 */
export default defineComponent({
	name: "SgnlTable",
	components: {
		Column16,
		ArrowUp16,
		ArrowDown16,
		ArrowsVertical16,
		ChevronLeft16,
		ChevronRight16,
		ErrorEmptyState,
		EmptyState,
		SkeletonBlock,
	},
	props: {
		columns: { type: Array as PropType<ColumnDef<any>[]>, required: true },
		data: { type: Array as PropType<any[]>, required: true },
		loading: { type: Boolean, default: false },
		emptyText: { type: String, default: "No data found." },
		emptyBody: { type: String, default: undefined },
		emptyIcon: { type: String, default: undefined },
		skeletonRowCount: { type: Number, default: 3 },
		// Sorting
		initialSorting: { type: Array as PropType<SortingState>, default: () => [] },
		// Column visibility
		initialColumnVisibility: { type: Object as PropType<VisibilityState>, default: () => ({}) },
		showColumnSelector: { type: Boolean, default: false },
		// Pagination
		paginated: { type: Boolean, default: false },
		pageSize: { type: Number, default: 10 },
		pageSizeOptions: { type: Array as PropType<number[]>, default: () => [10, 25, 50] },
		// Error state
		error: { type: Boolean, default: false },
		errorTitle: { type: String, default: "Could not load data" },
		errorMessage: { type: String, default: "" },
		retrying: { type: Boolean, default: false },
		// Row-level customization (all optional; default to plain rows)
		rowKey: { type: Function as PropType<(row: any) => string | number>, default: undefined },
		rowClass: { type: Function as PropType<(row: any) => any>, default: undefined },
		// Extra attributes (e.g. data-test) bound to each table row.
		rowAttrs: { type: Function as PropType<(row: any) => Record<string, any>>, default: undefined },
		// Viewport width (px) at or below which the table swaps to the card layout.
		cardBreakpoint: { type: Number, default: 719 },
	},
	emits: {
		retry: () => true,
		"row-click": (_row: any) => true,
	},
	setup(props) {
		// A computed ref keeps the table reactive to prop changes (the Vue adapter watches refs).
		// Only `data` is reactive in the adapter's contract (TableOptionsWithReactiveData); columns stay static.
		const dataRef = computed(() => props.data);

		const table = useVueTable({
			data: dataRef,
			columns: props.columns,
			getCoreRowModel: getCoreRowModel(),
			getSortedRowModel: getSortedRowModel(),
			...(props.paginated ? { getPaginationRowModel: getPaginationRowModel() } : {}),
			initialState: {
				sorting: props.initialSorting,
				columnVisibility: props.initialColumnVisibility,
				...(props.paginated ? { pagination: { pageIndex: 0, pageSize: props.pageSize } } : {}),
			},
		});

		return { table };
	},
	computed: {
		isCardLayout(): boolean {
			// Single source of the card breakpoint: gates both the table↔card swap and the column selector.
			return this.$vuetify.display.width <= this.cardBreakpoint;
		},
		hasCardSlot(): boolean {
			return !!this.$slots.card;
		},
		isEmpty(): boolean {
			// True only when the empty state is showing: not loading skeletons, and no rows.
			return !(this.loading && this.data.length === 0) && this.table.getRowModel().rows.length === 0;
		},
		hideableColumns() {
			return this.table.getAllLeafColumns().filter((column) => column.getCanHide());
		},
	},
	methods: {
		onPageSizeChange(event: Event) {
			const value = Number((event.target as HTMLSelectElement).value);
			this.table.setPageSize(value);
		},
	},
});
</script>

<style lang="scss" scoped>
.sgnl-table {
	position: relative;
}

.sgnl-table__column-btn {
	position: absolute;
	top: var(--sgnl-unit-1);
	right: var(--sgnl-unit-1);
	z-index: 1;
}

.sgnl-table__frame {
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-6);
	overflow: hidden;
}

/* === Table skin (mirrors Home/RecentlyCompletedTable) === */
.sgnl-data-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}

.sgnl-data-table thead tr {
	background: var(--sgnl-default-secondary-background-mono);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
}

.sgnl-data-table thead th {
	text-align: left;
	padding: var(--sgnl-space-8) var(--sgnl-space-16);
	font-weight: 500;
	color: var(--sgnl-default-secondary-text);
	font-size: var(--sgnl-font-size-caption);
	text-transform: uppercase;
	letter-spacing: var(--sgnl-font-letter-spacing-055);
	white-space: nowrap;
}

.sgnl-data-table thead th.sgnl-data-table__th--sortable {
	cursor: pointer;
	user-select: none;
}

.sgnl-data-table thead th.sgnl-data-table__th--sortable:hover {
	color: var(--sgnl-default-primary-text);
}

.sgnl-data-table__th-inner {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-4);
}

.sgnl-data-table__sort-icon {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	flex-shrink: 0;
	color: var(--sgnl-default-tertiary-icon);
}

.sgnl-data-table__sort-icon--active {
	color: var(--sgnl-default-primary-icon);
}

.sgnl-data-table tbody td {
	height: var(--sgnl-space-56);
	padding: var(--sgnl-space-4) var(--sgnl-space-16);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-tertiary-border);
	vertical-align: middle;
	color: var(--sgnl-default-primary-text);
}

.sgnl-data-table tbody tr:last-child td {
	border-bottom: none;
}

.sgnl-data-table tbody tr:not(.sgnl-data-table__empty-row):hover {
	background: var(--sgnl-default-background-hover);
}

.sgnl-data-table__empty {
	text-align: center;
	padding: 0;
}

/* === Mobile card layout === */
.sgnl-table__cards {
	display: none;
	flex-direction: column;
}

.sgnl-table__card {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-tertiary-border);
}

.sgnl-table__card:last-child {
	border-bottom: none;
}

.sgnl-table__card-empty {
	padding: 0;
	text-align: center;
}

/* === Pagination footer === */
.sgnl-table__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-8) var(--sgnl-space-16);
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	flex-wrap: wrap;
}

.sgnl-table__page-size {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
}

.sgnl-table__page-size-select {
	height: var(--sgnl-unit-28);
	padding: 0 var(--sgnl-space-8);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-4);
	background: var(--sgnl-default-primary-background);
	color: var(--sgnl-default-primary-text);
	cursor: pointer;
}

.sgnl-table__pager {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
}

.sgnl-table__page-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--sgnl-unit-28);
	height: var(--sgnl-unit-28);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-4);
	background: transparent;
	color: var(--sgnl-default-primary-icon);
	cursor: pointer;
}

.sgnl-table__page-btn:hover:not(:disabled) {
	background: var(--sgnl-default-background-hover);
}

.sgnl-table__page-btn:disabled {
	color: var(--sgnl-default-tertiary-icon);
	cursor: not-allowed;
}

/* Card mode is driven by the `isCardLayout` computed (single source of the breakpoint). */
.sgnl-table__frame--cards .sgnl-data-table {
	display: none;
}

.sgnl-table__frame--cards .sgnl-table__cards {
	display: flex;
}
</style>
